import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, CheckCircle2, Clock, AlertTriangle, ArrowRight, Info, Users,
  Truck, ClipboardCheck, Send, FileText, Lock, ShieldCheck, Sparkles, Eye, Bell
} from "lucide-react";

const inspectionStages = [
  {
    id: "footing",
    label: "Footing / Pre-Slab",
    readyToNotify: false,
    missingCerts: 2,
    tradeAtRisk: true,
    checklist: [
      { title: "Footing reinforcement proof", type: "proof", status: "needs-review", owner: "Site Team" },
      { title: "Drainage certificate", type: "certificate", status: "needs-review", owner: "David Patel" },
      { title: "Soil report verification", type: "certificate", status: "missing", owner: "David Patel" },
      { title: "Stormwater connection approval", type: "condition", status: "missing", owner: "Sarah Chen" },
      { title: "Slab formwork check", type: "proof", status: "missing", owner: "Site Team" },
    ],
    aiSummary: { risk: "high", gaps: 3, missing: 2, review: 2 },
  },
  {
    id: "frame",
    label: "Frame",
    readyToNotify: false,
    missingCerts: 0,
    tradeAtRisk: false,
    checklist: [
      { title: "Shadow diagrams update", type: "proof", status: "in-progress", owner: "Mark Stevens" },
      { title: "Neighbourhood character response", type: "condition", status: "not-started", owner: "Tom Bradley" },
      { title: "Frame tie-down certification", type: "certificate", status: "not-started", owner: "Engineer" },
    ],
    aiSummary: { risk: "medium", gaps: 2, missing: 1, review: 0 },
  },
  {
    id: "final",
    label: "Final / OC",
    readyToNotify: false,
    missingCerts: 0,
    tradeAtRisk: false,
    checklist: [
      { title: "Traffic response for driveway", type: "condition", status: "not-started", owner: "Emma Lawson" },
      { title: "Final landscaping plan", type: "proof", status: "not-started", owner: "Unassigned" },
      { title: "Occupancy certificate requirements", type: "certificate", status: "not-started", owner: "Unassigned" },
    ],
    aiSummary: { risk: "low", gaps: 3, missing: 0, review: 0 },
  },
];

const statusStyles = {
  "needs-review": { bg: "bg-amber-50", text: "text-amber-700", label: "Needs review" },
  "missing": { bg: "bg-red-50", text: "text-red-700", label: "Missing" },
  "in-progress": { bg: "bg-blue-50", text: "text-blue-700", label: "In progress" },
  "not-started": { bg: "bg-slate-100", text: "text-slate-500", label: "Not started" },
  "cleared": { bg: "bg-emerald-50", text: "text-emerald-700", label: "Cleared" },
};

export default function InspectionReadiness() {
  const [activeStage, setActiveStage] = useState("footing");
  const stage = inspectionStages.find(s => s.id === activeStage);

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="inspection-readiness-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Checks if the next inspection stage is ready before you notify the surveyor or release the next trade. Readiness and proof support, not certifier software.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Clear missing certificates and proof to make the stage ready. Then draft a surveyor notification or generate a surveyor-ready pack.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin. Answers: can we safely call the next stage yet?</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className={`border ${stage.readyToNotify ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"}`}>
            <CardContent className="py-3 px-4">
              <p className="text-xs font-medium text-slate-500">Ready to notify surveyor</p>
              <p className={`text-lg font-heading font-bold ${stage.readyToNotify ? "text-emerald-600" : "text-red-600"}`}>{stage.readyToNotify ? "Yes" : "No"}</p>
            </CardContent>
          </Card>
          <Card className={`border ${stage.missingCerts > 0 ? "bg-red-50 border-red-100" : "bg-emerald-50 border-emerald-100"}`}>
            <CardContent className="py-3 px-4">
              <p className="text-xs font-medium text-slate-500">Missing certificates</p>
              <p className={`text-lg font-heading font-bold ${stage.missingCerts > 0 ? "text-red-600" : "text-emerald-600"}`}>{stage.missingCerts}</p>
            </CardContent>
          </Card>
          <Card className={`border ${stage.tradeAtRisk ? "bg-amber-50 border-amber-100" : "bg-emerald-50 border-emerald-100"}`}>
            <CardContent className="py-3 px-4">
              <p className="text-xs font-medium text-slate-500">Trade mobilisation at risk</p>
              <p className={`text-lg font-heading font-bold ${stage.tradeAtRisk ? "text-amber-600" : "text-emerald-600"}`}>{stage.tradeAtRisk ? "Yes" : "No"}</p>
            </CardContent>
          </Card>
          <Card className="border bg-slate-50 border-slate-200">
            <CardContent className="py-3 px-4">
              <p className="text-xs font-medium text-slate-500">Current stage</p>
              <p className="text-lg font-heading font-bold text-slate-900">{stage.label}</p>
            </CardContent>
          </Card>
        </div>

        {/* Copilot entry */}
        <Link to="/builder/job-blocker-copilot" data-testid="copilot-entry-inspection"
          className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all group">
          <Sparkles className="w-4 h-4 text-slate-900 shrink-0" />
          <span className="text-xs font-medium text-slate-600 flex-1">Not ready for inspection? Ask the copilot what is missing and who to chase.</span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 transition-colors shrink-0" />
        </Link>

        <Tabs value={activeStage} onValueChange={setActiveStage}>
          <TabsList className="bg-slate-100 p-1 h-auto" data-testid="inspection-stage-tabs">
            {inspectionStages.map(s => (
              <TabsTrigger key={s.id} value={s.id} className="text-xs gap-1.5">
                <span className={`w-2 h-2 rounded-full ${s.readyToNotify ? "bg-emerald-500" : "bg-red-500"}`} />
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {inspectionStages.map(s => (
            <TabsContent key={s.id} value={s.id} className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Checklist */}
                <div className="lg:col-span-2 space-y-4">
                  <Card className="border-slate-200" data-testid="inspection-checklist">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-heading">Checklist for {s.label}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {s.checklist.map((item, i) => {
                        const ss = statusStyles[item.status];
                        return (
                          <div key={i} className="flex items-center justify-between py-3 px-4 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-all" data-testid={`checklist-item-${i}`}>
                            <div className="flex items-center gap-3">
                              {item.status === "cleared" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> :
                               item.status === "missing" ? <AlertTriangle className="w-5 h-5 text-red-500" /> :
                               item.status === "needs-review" ? <Eye className="w-5 h-5 text-amber-500" /> :
                               <Clock className="w-5 h-5 text-slate-400" />}
                              <div>
                                <p className="text-sm font-medium text-slate-900">{item.title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px]">{item.type}</Badge>
                                  <span className="text-xs text-slate-400">Owner: {item.owner}</span>
                                </div>
                              </div>
                            </div>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800" disabled={!s.readyToNotify} data-testid="notify-surveyor-btn">
                      <ClipboardCheck className="w-3.5 h-3.5" /> Draft surveyor notification
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5"><Send className="w-3.5 h-3.5" /> Draft request to engineer</Button>
                    <Button size="sm" variant="outline" className="gap-1.5"><FileText className="w-3.5 h-3.5" /> Generate surveyor-ready pack</Button>
                  </div>
                </div>

                {/* Right panel */}
                <div className="space-y-4">
                  {/* What is still missing */}
                  <Card className="border-red-200 bg-red-50/30" data-testid="missing-panel">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-heading text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> What is still missing
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {s.checklist.filter(c => c.status === "missing" || c.status === "needs-review").map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${item.status === "missing" ? "bg-red-500" : "bg-amber-500"}`} />
                          <div>
                            <p className="text-xs text-slate-700 font-medium">{item.title}</p>
                            <p className="text-[10px] text-slate-500">{item.status === "missing" ? "Not yet received" : "Proof arrived, needs review"}</p>
                          </div>
                        </div>
                      ))}
                      {s.checklist.filter(c => c.status === "missing" || c.status === "needs-review").length === 0 && (
                        <p className="text-xs text-slate-500">No missing items for this stage.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* AI Summary */}
                  <Card className="border-blue-200 bg-blue-50/20" data-testid="ai-summary-panel">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-heading text-blue-800 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> AI readiness summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Internal risk:</span>
                        <Badge className={`text-[10px] ${s.aiSummary.risk === "high" ? "bg-red-50 text-red-700 border-red-200" : s.aiSummary.risk === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {s.aiSummary.risk}
                        </Badge>
                      </div>
                      <p className="text-xs text-blue-700">{s.aiSummary.gaps} visible proof gaps</p>
                      <p className="text-xs text-blue-700">{s.aiSummary.missing} missing documents</p>
                      <p className="text-xs text-blue-700">{s.aiSummary.review} items need review</p>
                      <p className="text-[10px] text-blue-600 italic mt-2">This is a readiness and proof layer. Certification decisions stay outside easypermit.</p>
                    </CardContent>
                  </Card>

                  {/* Next best action */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-heading text-slate-600 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4" /> Next best action
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {s.checklist.find(c => c.status === "missing") ? (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-700">Chase {s.checklist.find(c => c.status === "missing")?.owner} for {s.checklist.find(c => c.status === "missing")?.title}</p>
                          <Button size="sm" variant="outline" className="text-xs gap-1"><Send className="w-3 h-3" /> Draft request</Button>
                        </div>
                      ) : s.checklist.find(c => c.status === "needs-review") ? (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-700">Review {s.checklist.find(c => c.status === "needs-review")?.title} in Proof Review</p>
                          <Button size="sm" variant="outline" className="text-xs gap-1"><Eye className="w-3 h-3" /> Go to review</Button>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">All items in progress or complete.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppLayout>
  );
}
